// src/components/Layout/Dashboard/Restaurant/ExportRestaurantModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, Calendar, Download, FileText } from 'lucide-react';

// Simple PDF library - No autoTable dependency issues
import jsPDF from 'jspdf';

const ExportRestaurantModal = ({ isOpen, onClose, onExport, totalRestaurants, theme }) => {
  const [selectedFields, setSelectedFields] = useState(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [dateRange, setDateRange] = useState('all');
  const [customDate, setCustomDate] = useState({ from: '', to: '' });
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const modalRef = useRef(null);

  const fieldCategories = {
    'Personal Info': [
      { id: 'id', label: 'ID', default: true },
      { id: 'restaurant_name', label: 'Restaurant Name', default: true },
      { id: 'contact_person_name', label: 'Contact Person Name', default: true },
      { id: 'prefix', label: 'Prefix', default: false },
      { id: 'gender', label: 'Gender', default: false },
      { id: 'contact', label: 'Contact', default: true },
      { id: 'email', label: 'Email', default: true },
      { id: 'password', label: 'Password', default: false },
      { id: 'org_type', label: 'Org Type', default: false },
    ],
    'Address': [
      { id: 'address', label: 'Address', default: true },
      { id: 'city', label: 'City', default: true },
      { id: 'state', label: 'State', default: true },
      { id: 'pin_code', label: 'PIN Code', default: true },
      { id: 'latitude', label: 'Latitude', default: true },
      { id: 'longitude', label: 'Longitude', default: true },
    ],
    'Owner Details': [
      { id: 'owner_name', label: 'Owner Name', default: true },
      { id: 'owner_mobile', label: 'Owner Mobile', default: false },
      { id: 'other_person_name', label: 'Other Person Name', default: false },
      { id: 'other_person_mobile', label: 'Other Person Mobile', default: false },
    ],
    'Documents': [
      { id: 'banner_image', label: 'Banner Image', default: false },
      { id: 'round_logo', label: 'Round Logo', default: false },
      { id: 'horizontal_logo', label: 'Horizontal Logo', default: false },
      { id: 'aadhar_front', label: 'Aadhar Front', default: false },
      { id: 'aadhar_back', label: 'Aadhar Back', default: false },
      { id: 'gst_number', label: 'GST Number', default: false },
      { id: 'food_license_image', label: 'Food License Image', default: false },
    ],
    'Bank Details': [
      { id: 'bank_name', label: 'Bank Name', default: false },
      { id: 'bank_branch', label: 'Branch', default: false },
      { id: 'bank_type', label: 'Bank Type', default: false },
      { id: 'account_holder', label: 'Account Holder', default: false },
      { id: 'account_number', label: 'Account Number', default: false },
      { id: 'ifsc', label: 'IFSC', default: false },
      { id: 'bank_mobile', label: 'Bank Mobile', default: false },
      { id: 'upi', label: 'UPI', default: false },
      { id: 'bank_passbook', label: 'Bank Passbook', default: false },
      { id: 'cancel_cheque', label: 'Cancel Cheque', default: false },
    ],
    'Business Settings': [
      { id: 'commission', label: 'Commission (%)', default: false },
      { id: 'special_commission', label: 'Special Commission (%)', default: false },
      { id: 'platform_charge', label: 'Platform Charge (%)', default: false },
      { id: 'handling_charge', label: 'Handling Charge (%)', default: false },
      { id: 'gst_percentage', label: 'GST (%)', default: false },
      { id: 'order_receiving_charges', label: 'Order Receiving Charges (%)', default: false },
      { id: 'min_order', label: 'Minimum Order', default: false },
      { id: 'wallet', label: 'Wallet', default: false },
      { id: 'paid_status', label: 'Paid Status', default: false },
      { id: 'mou', label: 'MOU', default: false },
      { id: 'subscription_id', label: 'Subscription ID', default: false },
      { id: 'referral_code', label: 'Referral Code', default: false },
      { id: 'referred_by', label: 'Referred By', default: false },
    ],
    'Status & Schedule': [
      { id: 'status', label: 'Status', default: true },
      { id: 'open_status', label: 'Open Status', default: false },
      { id: 'is_ready', label: 'Is Ready', default: false },
      { id: 'schedule_mode', label: 'Schedule Mode', default: false },
      { id: 'schedule_approved', label: 'Schedule Approved', default: false },
      { id: 'shift_start', label: 'Shift Start', default: false },
      { id: 'shift_end', label: 'Shift End', default: false },
    ],
    'Verification': [
      { id: 'personal_info_verified', label: 'Personal Info Verified', default: false },
      { id: 'documents_verified', label: 'Documents Verified', default: false },
      { id: 'bank_verified', label: 'Bank Verified', default: false },
      { id: 'remarks_personal', label: 'Remarks (Personal)', default: false },
      { id: 'remarks_documents', label: 'Remarks (Documents)', default: false },
      { id: 'remarks_bank', label: 'Remarks (Bank)', default: false },
    ],
    'Meta': [
      { id: 'created_at', label: 'Created At', default: false },
      { id: 'updated_at', label: 'Updated At', default: false },
      { id: 'remarks', label: 'Remarks', default: false },
    ]
  };

  useEffect(() => {
    if (isOpen) {
      const defaultFields = new Set();
      Object.values(fieldCategories).forEach(category => {
        category.forEach(field => {
          if (field.default) {
            defaultFields.add(field.id);
          }
        });
      });
      setSelectedFields(defaultFields);
      setSelectAll(true);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const toggleField = (fieldId) => {
    const newSelected = new Set(selectedFields);
    if (newSelected.has(fieldId)) {
      newSelected.delete(fieldId);
    } else {
      newSelected.add(fieldId);
    }
    setSelectedFields(newSelected);
    setSelectAll(newSelected.size === getTotalFields());
  };

  const toggleAll = () => {
    if (selectAll) {
      setSelectedFields(new Set());
      setSelectAll(false);
    } else {
      const allFields = new Set();
      Object.values(fieldCategories).forEach(category => {
        category.forEach(field => allFields.add(field.id));
      });
      setSelectedFields(allFields);
      setSelectAll(true);
    }
  };

  const resetDefaults = () => {
    const defaultFields = new Set();
    Object.values(fieldCategories).forEach(category => {
      category.forEach(field => {
        if (field.default) {
          defaultFields.add(field.id);
        }
      });
    });
    setSelectedFields(defaultFields);
    setSelectAll(defaultFields.size === getTotalFields());
  };

  const getTotalFields = () => {
    let count = 0;
    Object.values(fieldCategories).forEach(category => {
      count += category.length;
    });
    return count;
  };

  const handleExport = () => {
    const fields = Array.from(selectedFields);
    onExport({
      fields,
      dateRange,
      customDate,
      format: exportFormat
    });
  };

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'prev_week', label: 'Prev Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'prev_month', label: 'Prev Month' },
    { value: 'custom', label: 'Custom' },
  ];

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '20px',
    }}>
      <div ref={modalRef} style={{
        background: theme.surface,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '650px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: theme.textPrimary }}>
              Export Restaurants
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: theme.textMuted }}>
              Pick fields to include. Respects current search & filters.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.textMuted,
              padding: '4px',
              borderRadius: '6px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = theme.primaryLight; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{
          padding: '16px 24px',
          overflowY: 'auto',
          flex: 1,
        }}>
          {/* Format Selection */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
          }}>
            <button
              onClick={() => setExportFormat('csv')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: `2px solid ${exportFormat === 'csv' ? theme.primary : theme.border}`,
                background: exportFormat === 'csv' ? theme.primaryLight : 'transparent',
                color: exportFormat === 'csv' ? theme.primary : theme.textSecondary,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
              }}
            >
              <Download size={16} />
              CSV
            </button>
            <button
              onClick={() => setExportFormat('pdf')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: `2px solid ${exportFormat === 'pdf' ? theme.primary : theme.border}`,
                background: exportFormat === 'pdf' ? theme.primaryLight : 'transparent',
                color: exportFormat === 'pdf' ? theme.primary : theme.textSecondary,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s',
              }}
            >
              <FileText size={16} />
              PDF
            </button>
          </div>

          {/* Stats and Controls */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            marginBottom: '16px',
            padding: '12px 16px',
            background: theme.primaryLight,
            borderRadius: '10px',
          }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: theme.primary }}>
              {selectedFields.size} fields selected
            </span>
            <div style={{ flex: 1 }} />
            <button
              onClick={toggleAll}
              style={{
                padding: '4px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                background: 'transparent',
                color: theme.textPrimary,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500,
              }}
            >
              {selectAll ? 'Select None' : 'Select All'}
            </button>
            <button
              onClick={resetDefaults}
              style={{
                padding: '4px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                background: 'transparent',
                color: theme.textPrimary,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500,
              }}
            >
              Reset Defaults
            </button>
          </div>

          {/* Date Range */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.border}`,
                  background: theme.surface,
                  color: theme.textPrimary,
                  cursor: 'pointer',
                  fontSize: '12px',
                  width: 'auto',
                }}
              >
                <Calendar size={14} />
                <span>Date Range (Joined / Created):</span>
                <span style={{ fontWeight: 600 }}>{dateRangeOptions.find(d => d.value === dateRange)?.label}</span>
                <ChevronDown size={14} style={{ marginLeft: 'auto' }} />
              </button>
              {isDateDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  background: theme.surface,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  minWidth: '180px',
                  zIndex: 10,
                  overflow: 'hidden',
                }}>
                  {dateRangeOptions.map(option => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setDateRange(option.value);
                        setIsDateDropdownOpen(false);
                      }}
                      style={{
                        padding: '8px 14px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: dateRange === option.value ? theme.primary : theme.textPrimary,
                        background: dateRange === option.value ? theme.primaryLight : 'transparent',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        if (dateRange !== option.value) {
                          e.currentTarget.style.background = theme.primaryLight;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (dateRange !== option.value) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {dateRange === 'custom' && (
              <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                <input
                  type="date"
                  value={customDate.from}
                  onChange={(e) => setCustomDate({ ...customDate, from: e.target.value })}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${theme.border}`,
                    background: theme.surface,
                    color: theme.textPrimary,
                    fontSize: '12px',
                  }}
                />
                <span style={{ color: theme.textMuted, alignSelf: 'center' }}>to</span>
                <input
                  type="date"
                  value={customDate.to}
                  onChange={(e) => setCustomDate({ ...customDate, to: e.target.value })}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${theme.border}`,
                    background: theme.surface,
                    color: theme.textPrimary,
                    fontSize: '12px',
                  }}
                />
              </div>
            )}
          </div>

          {/* Field Categories */}
          {Object.entries(fieldCategories).map(([category, fields]) => (
            <div key={category} style={{ marginBottom: '16px' }}>
              <h4 style={{
                fontSize: '13px',
                fontWeight: 700,
                color: theme.primary,
                margin: '0 0 8px 0',
                paddingBottom: '6px',
                borderBottom: `1px solid ${theme.border}`,
              }}>
                {category}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                {fields.map(field => (
                  <label
                    key={field.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: theme.textPrimary,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = theme.primaryLight;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFields.has(field.id)}
                      onChange={() => toggleField(field.id)}
                      style={{
                        accentColor: theme.primary,
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                      }}
                    />
                    {field.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${theme.border}`,
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              borderRadius: '8px',
              border: `1px solid ${theme.border}`,
              background: 'transparent',
              color: theme.textSecondary,
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            style={{
              padding: '8px 20px',
              borderRadius: '8px',
              border: 'none',
              background: theme.primary,
              color: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme.primaryDark || theme.primary;
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.primary;
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {exportFormat === 'csv' ? <Download size={16} /> : <FileText size={16} />}
            Download {exportFormat.toUpperCase()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportRestaurantModal;